import React, { useState, useEffect, useMemo, useCallback, useLayoutEffect } from "react";
import { View, StyleSheet, TextInput, Pressable, Platform, ScrollView } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import useDateTime from "@/hooks/useDateTime";
import useAuth from "@/hooks/useAuth";
import usePermissions from "@/hooks/usePermissions";
import { useCurrentEntity } from "@/contexts/CurrentEntityContext";
import { useNavigationHistory } from "@/contexts/NavigationHistoryContext";
import { projectService } from "@/services/projectService";
import { ProjectType } from "@/types/db/projects";
import useNavigationGuard from "@/hooks/useNavigationGuard";
import { useNavigation } from "@react-navigation/native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import {
  Table,
  ColumnDef,
  EditableOption,
  ProgressCell,
  calculateProgress,
  DurationCell,
  calculateDuration,
} from "@/components/NewTable";
import { theme } from "@/constants/themes";

// Status color mapping for projects
const STATUS_COLORS: Record<string, keyof Pick<theme, "complete" | "pending" | "attention" | "neutral">> = {
  POTENTIAL: "neutral",
  PENDING: "pending",
  "PENDING APPROVAL": "pending",
  ACTIVE: "complete",
  "IN PROGRESS": "complete",
  COMPLETED: "complete",
  "ON-HOLD": "attention",
  "ON HOLD": "attention",
  CANCELLED: "attention",
  CANCELED: "attention",
};

export interface RelatedProjectsScreenProps {
  showInactive?: boolean;
}

export function RelatedProjectsScreen({ showInactive = true }: RelatedProjectsScreenProps) {
  const { colors } = useTheme();
  const { formatDate } = useDateTime();
  const { displayNames } = useAuth();
  const { projects: projectPermissions } = usePermissions();
  const currentEntity = useCurrentEntity();
  const { pushNavigation } = useNavigationHistory();
  const navigation = useNavigation();
  const { navigateToEntity } = useNavigationGuard();
  const [projects, setProjects] = useState<ProjectType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState("");

  // Set up header with search
  useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => (
        <View style={styles.headerTitleContainer}>
          <ThemedText style={styles.headerTitle}>{displayNames?.projects?.plural || "Projects"}</ThemedText>
          <View style={[styles.headerSearchContainer, { backgroundColor: colors.backgroundSecondary }]}>
            <MaterialCommunityIcons
              name="magnify"
              size={16}
              color={colors.textSecondary}
              style={styles.headerSearchIcon}
            />
            <TextInput
              value={searchValue}
              onChangeText={setSearchValue}
              placeholder="Search..."
              placeholderTextColor={colors.textSecondary}
              style={[styles.headerSearchInput, { color: colors.text }]}
              autoCapitalize="none"
              autoCorrect={false}
            />
            {searchValue.length > 0 && (
              <Pressable onPress={() => setSearchValue("")} style={styles.headerClearButton}>
                <MaterialCommunityIcons name="close-circle" size={14} color={colors.textSecondary} />
              </Pressable>
            )}
          </View>
        </View>
      ),
    });
  }, [navigation, searchValue, colors, displayNames?.projects?.plural]);

  useEffect(() => {
    const fetchProjects = async () => {
      if (!currentEntity?.currentId || !currentEntity?.currentPanel) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        let fetchedProjects: ProjectType[] = [];

        switch (currentEntity.currentPanel) {
          case "client":
            fetchedProjects = await projectService.getProjectsByClientId(currentEntity.currentId);
            break;
          default:
            console.warn(`No project relationship defined for entity type: ${currentEntity.currentPanel}`);
        }

        setProjects(fetchedProjects);
      } catch (err: any) {
        console.error("Failed to fetch related projects:", err);
        setError(err.message || "Failed to load projects");
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [currentEntity?.currentId, currentEntity?.currentPanel]);

  // Filter projects based on completed toggle (time-based, not status)
  const filteredProjects = useMemo(() => {
    if (showInactive) {
      return projects;
    }
    // Filter to only non-completed projects (progress < 100%)
    return projects.filter((project) => {
      return calculateProgress(project.start_time, project.end_time) < 100;
    });
  }, [projects, showInactive]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getStatusColor = (status?: string) => {
    const colorKey = STATUS_COLORS[status?.toUpperCase() || "PENDING"] || "pending";
    return colors[colorKey].border;
  };

  // Status options for project editable picker
  const statusOptions: EditableOption[] = useMemo(
    () => [
      { value: "POTENTIAL", label: "Potential", color: colors.neutral.border },
      { value: "PENDING", label: "Pending", color: colors.pending.border },
      { value: "ACTIVE", label: "Active", color: colors.complete.border },
      { value: "ON-HOLD", label: "On Hold", color: colors.attention.border },
      { value: "COMPLETED", label: "Completed", color: colors.complete.border },
      { value: "CANCELED", label: "Canceled", color: colors.attention.border },
    ],
    [colors],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const formatCurrency = (amount?: number) => {
    if (!amount) return "—";
    return `$${amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Handle edit callback for project fields
  const handleEdit = useCallback(async (row: ProjectType, key: string, newValue: string) => {
    if (!row.id) return;

    try {
      await projectService.updateProject(row.id, { [key]: newValue });
      setProjects((prev) => prev.map((project) => (project.id === row.id ? { ...project, [key]: newValue } : project)));
    } catch (err) {
      console.error("Failed to update project:", err);
    }
  }, []);

  // Handle delete callback
  const handleDelete = useCallback(async (row: ProjectType) => {
    if (!row.id) return;

    try {
      await projectService.deleteProject(row.id);
      setProjects((prev) => prev.filter((project) => project.id !== row.id));
    } catch (err) {
      console.error("Failed to delete project:", err);
    }
  }, []);

  const handleProjectPress = useCallback(
    (project: ProjectType) => {
      if (currentEntity?.currentPanel && currentEntity?.currentId) {
        pushNavigation({
          panel: currentEntity.currentPanel,
          id: currentEntity.currentId,
          title: currentEntity.currentTitle || currentEntity.currentPanel,
        });
      }
      navigateToEntity("project", project.id!);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentEntity, pushNavigation],
  );

  // Define columns for the table
  const columns: ColumnDef<ProjectType>[] = useMemo(
    () => [
      {
        key: "name",
        header: "Name",
        flex: 1,
        minWidth: 150,
        accessor: (row) => row.name || "Untitled Project",
        onPress: (row) => handleProjectPress(row),
        editable: {
          type: "text",
          placeholder: "Project name",
        },
      },
      {
        key: "status",
        header: "Status",
        width: 180,
        align: "center",
        searchable: true,
        searchValue: (_, row) => row.status || "PENDING",
        editable: {
          type: "picker",
          options: statusOptions,
          placeholder: "Select status",
        },
        render: (value, row) => (
          <View style={[styles.statusPill, { backgroundColor: getStatusColor(row.status) }]}>
            <ThemedText style={styles.statusText}>{row.status?.toUpperCase() || "PENDING"}</ThemedText>
          </View>
        ),
      },
      {
        key: "progress",
        header: "Progress",
        width: 100,
        align: "center",
        searchable: false,
        accessor: (row) => calculateProgress(row.start_time, row.end_time),
        render: (value, row) => <ProgressCell startTime={row.start_time} endTime={row.end_time} />,
      },
      {
        key: "budget",
        header: "Budget",
        width: 120,
        align: "right",
        searchable: false,
        accessor: (row) => (row as any).budget,
        render: (value) => <ThemedText style={styles.valueText}>{formatCurrency(value)}</ThemedText>,
      },
      {
        key: "start_time",
        header: "Start",
        width: 110,
        searchable: false,
        accessor: (row) => row.start_time,
        render: (value) => <ThemedText style={styles.dateText}>{value ? formatDate(value) : "—"}</ThemedText>,
      },
      {
        key: "end_time",
        header: "End",
        width: 110,
        searchable: false,
        accessor: (row) => row.end_time,
        render: (value) => <ThemedText style={styles.dateText}>{value ? formatDate(value) : "—"}</ThemedText>,
      },
      {
        key: "duration",
        header: "Duration",
        width: 100,
        align: "right",
        searchable: false,
        accessor: (row) => calculateDuration(row.start_time, row.end_time),
        render: (value) => <DurationCell hours={value} />,
      },
    ],
    [formatDate, getStatusColor, statusOptions, formatCurrency, handleProjectPress],
  );

  const projectsName = displayNames?.projects?.plural?.toLowerCase() || "projects";

  if (error) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={styles.emptyState}>
          <MaterialCommunityIcons name="alert-circle" size={48} color={colors.red.text} />
          <ThemedText style={styles.emptyText}>Failed to load {projectsName}</ThemedText>
          <ThemedText style={{ opacity: 0.6, fontSize: 12 }}>{error}</ThemedText>
        </View>
      </View>
    );
  }

  return (
    <ScrollView
      horizontal={true}
      showsHorizontalScrollIndicator={true}
      contentContainerStyle={{ width: "auto", flexGrow: 1 }}
      style={[styles.container, { backgroundColor: colors.background }]}
    >
      <Table<ProjectType>
        data={filteredProjects}
        columns={columns}
        keyExtractor={(item) => item.id || ""}
        loading={loading}
        loadingRowCount={8}
        onEdit={handleEdit}
        canEdit={(row) => projectPermissions.update && calculateProgress(row.start_time, row.end_time) < 100}
        onDelete={handleDelete}
        canDelete={(row) => projectPermissions.delete && calculateProgress(row.start_time, row.end_time) < 100}
        searchValue={searchValue}
        sortable
        defaultSort={{ key: "name", direction: "asc" }}
        emptyMessage={`No ${projectsName} found`}
        emptyIcon="folder-outline"
      />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerTitleContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: "600",
  },
  headerSearchContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
    minWidth: 140,
  },
  headerSearchIcon: {
    marginRight: 6,
  },
  headerSearchInput: {
    flex: 1,
    fontSize: 14,
    minWidth: 100,
    ...Platform.select({
      web: { outlineStyle: "none" as any },
    }),
  },
  headerClearButton: {
    padding: 2,
    marginLeft: 4,
  },
  statusPill: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  statusText: {
    fontSize: 10,
    fontWeight: "600",
    color: "white",
  },
  dateText: {
    fontSize: 12,
  },
  valueText: {
    fontSize: 12,
  },
  emptyState: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
    gap: 8,
  },
  emptyText: {
    fontSize: 16,
    fontWeight: "600",
    opacity: 0.6,
  },
});
